import { useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { cn } from '../ui';
import { ToolButton, ToolIcon, toolDef, TOOL_GROUPS, type IconId, type ToolGroupId, type ToolId } from './DiagramTools';

/**
 * Kompakte Werkzeugleiste des Skizzeneditors: Auswaehlen (mit Mehrfach) · Spieler · Material · Wege · Zone · Text · Mass · Einfuegen.
 *
 * Spieler, Material, Wege und Einfuegen sind Sammelknoepfe: der Hauptknopf zeigt Symbol und Namen des zuletzt gewaehlten
 * Werkzeugs der Gruppe und aktiviert es mit einem Tipp sofort; der kleine Pfeil daneben (oder langes Druecken) oeffnet ein
 * Auswahlfeld mit allen Werkzeugen der Gruppe. Das zuletzt Gewaehlte je Gruppe wird im Geraetespeicher gemerkt.
 * Waagerecht (eine Zeile, scrollt am Handy seitwaerts) oder senkrecht (schmale Leiste, Beschriftung klein unter dem Symbol).
 */

export type ToolbarOrientation = 'horizontal' | 'vertical';

/** Aktionen der Gruppe "Einfuegen" - keine Werkzeuge, sie oeffnen einen Dialog. */
type InsertId = 'setup' | 'formation';
const INSERTS: readonly { id: InsertId; label: string; short: string; icon: IconId }[] = [
  { id: 'setup', label: 'Aufbau einfügen', short: 'Aufbau', icon: 'setup' },
  { id: 'formation', label: 'Formation einfügen', short: 'Formation', icon: 'formation' },
];

type ToolSplitId = 'players' | 'material' | 'paths';
type SplitGroupId = ToolSplitId | 'insert';
const titleOf = (id: ToolGroupId): string => TOOL_GROUPS.find((group) => group.id === id)?.title ?? id;
const toolsOf = (id: ToolGroupId): readonly ToolId[] => TOOL_GROUPS.find((group) => group.id === id)?.tools ?? [];

export const LAST_TOOLS_KEY = 'ukinfo.skizze.werkzeuge';
const LONG_PRESS_MS = 500;

interface Remembered {
  players: ToolId;
  material: ToolId;
  paths: ToolId;
  insert: InsertId;
}

const DEFAULTS: Remembered = { players: 'playerA', material: 'cone', paths: 'arrowPASS', insert: 'setup' };

function readRemembered(): Remembered {
  try {
    const raw: unknown = JSON.parse(window.localStorage.getItem(LAST_TOOLS_KEY) ?? 'null');
    if (raw && typeof raw === 'object') {
      const data = raw as Record<string, unknown>;
      const pick = (group: ToolSplitId): ToolId => toolsOf(group).find((id) => id === data[group]) ?? DEFAULTS[group];
      const insert = INSERTS.find((entry) => entry.id === data.insert)?.id ?? DEFAULTS.insert;
      return { players: pick('players'), material: pick('material'), paths: pick('paths'), insert };
    }
  } catch {
    // Ohne Speicher (privater Modus, gesperrt) gelten die Standardwerkzeuge.
  }
  return DEFAULTS;
}

function writeRemembered(value: Remembered): void {
  try {
    window.localStorage.setItem(LAST_TOOLS_KEY, JSON.stringify(value));
  } catch {
    // Nicht gemerkt ist nicht schlimm.
  }
}

export interface ToolPanelProps {
  tool: ToolId;
  onTool: (id: ToolId) => void;
  /** Umschalter "Mehrfach" neben "Auswaehlen". */
  multi: boolean;
  onToggleMulti: () => void;
  disabled?: boolean;
  onInsertSetup: () => void;
  onInsertFormation: () => void;
  /** Standard: waagerecht. Senkrecht ist die schmale Leiste fuer den Vollbildmodus. */
  orientation?: ToolbarOrientation;
}

interface MenuEntry {
  key: string;
  label: string;
  short: string;
  icon: IconId;
  checked: boolean;
  /** Aktionen (Einfuegen) sind keine Umschalter. */
  action: boolean;
}

interface Popover {
  group: SplitGroupId;
  top: number;
  left: number;
}

export function ToolPanel({ tool, onTool, multi, onToggleMulti, disabled, onInsertSetup, onInsertFormation, orientation = 'horizontal' }: ToolPanelProps): JSX.Element {
  const vertical = orientation === 'vertical';
  const [last, setLast] = useState<Remembered>(readRemembered);
  const [popover, setPopover] = useState<Popover | null>(null);
  const arrowRefs = useRef<Partial<Record<SplitGroupId, HTMLButtonElement | null>>>({});
  const mainRefs = useRef<Partial<Record<SplitGroupId, HTMLButtonElement | null>>>({});
  const menuRef = useRef<HTMLDivElement | null>(null);
  const longPress = useRef<{ timer: number | null; fired: boolean }>({ timer: null, fired: false });

  // Wird ein Werkzeug von aussen oder aus dem Auswahlfeld aktiv, merken wir es fuer seine Gruppe.
  useEffect(() => {
    const group = (['players', 'material', 'paths'] as const).find((id) => toolsOf(id).includes(tool));
    if (!group) return;
    setLast((current) => {
      if (current[group] === tool) return current;
      const next = { ...current, [group]: tool };
      writeRemembered(next);
      return next;
    });
  }, [tool]);

  const closePopover = (focusGroup?: SplitGroupId, target: 'arrow' | 'main' = 'arrow'): void => {
    setPopover(null);
    if (focusGroup) (target === 'arrow' ? arrowRefs : mainRefs).current[focusGroup]?.focus();
  };

  const openGroup = popover?.group;

  // Ausserhalb tippen, scrollen oder die Fenstergroesse aendern schliesst das Feld.
  useEffect(() => {
    if (!openGroup) return;
    const onPointer = (event: Event): void => {
      const node = event.target as Node | null;
      if (node && (menuRef.current?.contains(node) || arrowRefs.current[openGroup]?.contains(node) || mainRefs.current[openGroup]?.contains(node))) return;
      setPopover(null);
    };
    const close = (): void => setPopover(null);
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('mousedown', onPointer);
    window.addEventListener('resize', close);
    window.addEventListener('scroll', close, true);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('mousedown', onPointer);
      window.removeEventListener('resize', close);
      window.removeEventListener('scroll', close, true);
    };
  }, [openGroup]);

  // Beim Oeffnen springt der Fokus auf den gewaehlten (sonst ersten) Eintrag.
  useEffect(() => {
    if (!openGroup) return;
    const items = Array.from(menuRef.current?.querySelectorAll<HTMLElement>('[role^="menuitem"]') ?? []);
    (items.find((item) => item.getAttribute('aria-checked') === 'true') ?? items[0])?.focus();
  }, [openGroup]);

  const openPopover = (group: SplitGroupId): void => {
    const rect = mainRefs.current[group]?.parentElement?.getBoundingClientRect();
    // Waagerecht unter dem Knopf, senkrecht rechts daneben; im Fenster halten (das Feld ist hoechstens ca. 14 rem breit).
    const rawLeft = rect ? (vertical ? rect.right + 6 : rect.left) : 0;
    const rawTop = rect ? (vertical ? rect.top : rect.bottom + 6) : 0;
    setPopover({ group, left: Math.max(8, Math.min(rawLeft, window.innerWidth - 232)), top: Math.max(8, Math.min(rawTop, window.innerHeight - 60)) });
  };

  const entriesOf = (group: SplitGroupId): MenuEntry[] =>
    group === 'insert'
      ? INSERTS.map((entry) => ({ key: entry.id, label: entry.label, short: entry.short, icon: entry.icon, checked: last.insert === entry.id, action: true }))
      : toolsOf(group).map((id) => {
          const def = toolDef(id);
          return { key: id, label: def.label, short: def.name, icon: id, checked: tool === id, action: false };
        });

  const runInsert = (id: InsertId): void => {
    if (id === 'setup') onInsertSetup();
    else onInsertFormation();
  };

  const choose = (group: SplitGroupId, key: string): void => {
    closePopover(group, 'main');
    if (group === 'insert') {
      const id = key as InsertId;
      setLast((current) => {
        const next = { ...current, insert: id };
        writeRemembered(next);
        return next;
      });
      runInsert(id);
    } else {
      onTool(key as ToolId);
    }
  };

  const onMenuKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>, group: SplitGroupId): void => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      closePopover(group);
      return;
    }
    if (event.key === 'Tab') {
      setPopover(null);
      return;
    }
    const items = Array.from(event.currentTarget.querySelectorAll<HTMLElement>('[role^="menuitem"]'));
    const index = items.indexOf(document.activeElement as HTMLElement);
    const move = (to: number): void => {
      event.preventDefault();
      items[(to + items.length) % items.length]?.focus();
    };
    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') move(index + 1);
    else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') move(index - 1);
    else if (event.key === 'Home') move(0);
    else if (event.key === 'End') move(items.length - 1);
  };

  const cell = 'shrink-0 rounded-xl border text-[0.7rem] font-medium leading-tight shadow-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/60 disabled:cursor-not-allowed disabled:opacity-45';
  const stack = cn('flex min-h-11 flex-col items-center justify-center gap-0.5 px-1 py-1', vertical ? 'w-full text-[0.62rem]' : 'min-w-[3.5rem]');
  const tone = (active: boolean): string => (active ? 'border-brand-600 bg-brand-600 text-white' : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50');

  const single = (id: ToolId): JSX.Element => {
    const def = toolDef(id);
    return <ToolButton key={id} label={def.label} short={def.short} icon={id} pressed={tool === id} fill={vertical} disabled={disabled} onClick={() => onTool(id)} />;
  };

  const clearTimer = (): void => {
    if (longPress.current.timer !== null) window.clearTimeout(longPress.current.timer);
    longPress.current.timer = null;
  };
  useEffect(() => clearTimer, []);

  const split = (group: SplitGroupId): JSX.Element => {
    const insertDef = INSERTS.find((entry) => entry.id === last.insert) ?? INSERTS[0]!;
    const def = group === 'insert' ? null : toolDef(last[group]);
    const label = def ? def.label : insertDef.label;
    const short = def ? def.short : insertDef.short;
    const icon: IconId = def ? def.id : insertDef.icon;
    const active = group !== 'insert' && toolsOf(group).includes(tool);
    const isOpen = popover?.group === group;
    return (
      <div key={group} role="group" aria-label={titleOf(group)} data-testid={`tool-group-${group}`} className={cn('flex shrink-0', vertical ? 'w-full flex-col' : 'flex-row')}>
        <button
          ref={(node) => {
            mainRefs.current[group] = node;
          }}
          type="button"
          aria-label={label}
          title={label}
          aria-pressed={group === 'insert' ? undefined : active}
          disabled={disabled}
          onPointerDown={() => {
            longPress.current.fired = false;
            clearTimer();
            longPress.current.timer = window.setTimeout(() => {
              longPress.current.timer = null;
              longPress.current.fired = true;
              openPopover(group);
            }, LONG_PRESS_MS);
          }}
          onPointerUp={clearTimer}
          onPointerLeave={clearTimer}
          onPointerCancel={clearTimer}
          onContextMenu={(event) => event.preventDefault()}
          onClick={() => {
            if (longPress.current.fired) {
              longPress.current.fired = false;
              return;
            }
            setPopover(null);
            if (group === 'insert') runInsert(last.insert);
            else onTool(last[group]);
          }}
          className={cn(cell, stack, tone(active), vertical ? 'rounded-b-none' : 'rounded-r-none')}
        >
          <ToolIcon id={icon} />
          <span>{short}</span>
        </button>
        <button
          ref={(node) => {
            arrowRefs.current[group] = node;
          }}
          type="button"
          aria-label={`Werkzeuggruppe ${titleOf(group)}`}
          title={`Alle Werkzeuge: ${titleOf(group)}`}
          aria-haspopup="menu"
          aria-expanded={isOpen}
          disabled={disabled}
          onClick={() => (isOpen ? closePopover(group) : openPopover(group))}
          onKeyDown={(event) => {
            if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && !isOpen) {
              event.preventDefault();
              openPopover(group);
            }
          }}
          className={cn(cell, 'flex items-center justify-center', tone(active || isOpen), vertical ? 'min-h-6 w-full rounded-t-none border-t-0' : 'min-h-11 w-7 rounded-l-none border-l-0')}
        >
          <svg viewBox="0 0 10 6" aria-hidden="true" className={cn('h-2 w-3 shrink-0 transition-transform', isOpen && 'rotate-180')}>
            <path d="M1 1l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>
    );
  };

  return (
    <section aria-label="Werkzeuge" data-testid="tool-panel" data-orientation={orientation}>
      <div
        role="toolbar"
        aria-label="Werkzeugleiste"
        aria-orientation={orientation}
        data-testid="tool-bar"
        className={cn('gap-1.5', vertical ? 'flex w-16 flex-col' : 'flex flex-row items-stretch overflow-x-auto pb-1')}
      >
        <div role="group" aria-label="Auswählen" data-testid="tool-group-select" className={cn('flex shrink-0 gap-1', vertical ? 'w-full flex-col' : 'flex-row')}>
          {single('select')}
          <ToolButton label="Mehrfachauswahl" short="Mehrfach" icon="multi" pressed={multi} fill={vertical} disabled={disabled} onClick={onToggleMulti} />
        </div>
        {split('players')}
        {split('material')}
        {split('paths')}
        {single('zone')}
        {single('text')}
        {single('measure')}
        {split('insert')}
      </div>

      {popover && (
        <div
          ref={menuRef}
          role="menu"
          aria-label={titleOf(popover.group)}
          data-testid="tool-menu"
          style={{ position: 'fixed', top: popover.top, left: popover.left }}
          onKeyDown={(event) => onMenuKeyDown(event, popover.group)}
          className="z-50 flex max-h-[70vh] min-w-[11rem] flex-col gap-1 overflow-y-auto rounded-xl border border-slate-300 bg-white p-1.5 shadow-lg"
        >
          {entriesOf(popover.group).map((entry) => (
            <button
              key={entry.key}
              type="button"
              role={entry.action ? 'menuitem' : 'menuitemradio'}
              aria-checked={entry.action ? undefined : entry.checked}
              aria-label={entry.label}
              title={entry.label}
              tabIndex={-1}
              onClick={() => choose(popover.group, entry.key)}
              className={cn(
                'flex min-h-11 items-center gap-2 rounded-lg px-2.5 py-1 text-left text-sm font-medium',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/60',
                entry.checked ? 'bg-brand-50 text-brand-700 ring-1 ring-brand-600' : 'text-slate-700 hover:bg-slate-100',
              )}
            >
              <ToolIcon id={entry.icon} />
              <span>{entry.short}</span>
            </button>
          ))}
        </div>
      )}
    </section>
  );
}
